/**
 * StrategicSimulator — "The autoplay running at infinite speed."
 *
 * Uses the shared BotStrategy module for firing sequence, lap simulation,
 * and depth-N lookahead dispatch — identical logic to AutoplaySystem.
 *
 * Lane-based queue: configurable lanes (default 3), front-only access.
 * Multi-orb attackers span adjacent lanes.
 *
 * FAST: No Phaser, no real-time ticks. Each "tick" analytically computes
 * a full lap result by scanning firing lines and doing array arithmetic.
 */

import type { CellStack, MegaBlockDef, BlockadeWallDef, LockedDoorDef } from '../types/board';
import { cellKey } from '../types/board';
import { CONVEYOR_MAX_ATTACKERS, MAX_STACK_SLOTS, DEFAULT_NUM_QUEUES, DEFAULT_LOOKAHEAD_DEPTH, SOLVE_ROLLOUT_BUDGET } from '../config/constants';
import type { AttackerQueueEntry } from './LevelGenerator';
import {
  buildFiringSequence,
  buildReachMap,
  simulateLap,
  evaluateDispatch,
  createBotTrace,
  type FiringLine,
  type BoardSnapshot,
  type BotAttacker,
  type BotMegaBlock,
  type BotWall,
  type BotDoor,
  type DispatchCandidate,
  type BotTrace,
  type TraceEntry,
} from './BotStrategy';

import { evaluateBoardState, type BoardStateSnapshot } from './BoardStateEvaluator';

// Re-export SimulatorOptions from LevelSimulator for consistency
export type { SimulatorOptions } from './LevelSimulator';
import type { SimulatorOptions } from './LevelSimulator';

// ── Types ────────────────────────────────────────────────────────────

/** Outcome of a single simulation run with rich detail. */
export type SimOutcome = 'win' | 'loss' | 'timeout' | 'stall';

export interface SimulationResult {
  outcome: SimOutcome;
  /** Total laps (ticks) executed. */
  lapsExecuted: number;
  /** Total cells on the board at start. */
  totalCells: number;
  /** Cells remaining at end of simulation. */
  cellsRemaining: number;
  /** Percentage of board cleared (0-100). */
  percentCleared: number;
  /** For stall: how many consecutive zero-progress laps triggered the stall. */
  stallLaps?: number;
  /** Stack attacker count at end. */
  stackSize?: number;
  /** Queue attacker count at end. */
  queueRemaining?: number;
}

interface SimOrb {
  colorIndex: number;
  ammo: number;
}

interface SimAttacker {
  orbs: SimOrb[];
  size: number;
  frozen: number;
}

// ── Mid-Game State (for runtime autoplay integration) ────────────────

/** Sim-compatible attacker shape for mid-game state injection. */
export interface SimAttackerData {
  orbs: { colorIndex: number; ammo: number }[];
  size: number;
  frozen: number;
}

/** Mid-game state snapshot for constructing a simulator from live game state. */
export interface MidGameState {
  grid: CellStack[][];
  /** Queue lanes: each lane is an array of SimAttackerData (front-first). */
  queueLanes: (SimAttackerData | null)[][];
  conveyor: SimAttackerData[];
  stack: SimAttackerData[];
  maxConveyor: number;
  maxStack: number;
  numQueues: number;
  snakeCells?: Set<number>;
  megaBlockDefs?: MegaBlockDef[];
  doorCells?: Set<number>;
  barricadeCells?: Set<number>;
  /** Active blockade walls (current HP) to seed bot simulation with. */
  blockadeWalls?: BotWall[];
  /** Active locked doors (current HP) to seed bot simulation with. */
  lockedDoors?: BotDoor[];
  lookaheadDepth?: number;
  rolloutDepth?: number;
}

/** Result of a single dispatch decision from the simulator. */
export interface DispatchDecision {
  action: 'dispatch_stack' | 'dispatch_queue' | 'wait';
  /** Stack slot index or queue lane index, depending on action. */
  index?: number;
}

// ── Strategic Simulator ──────────────────────────────────────────────

export class StrategicSimulator {
  static isWinnable(
    grid: CellStack[][],
    queue: AttackerQueueEntry[],
    maxConveyor: number = CONVEYOR_MAX_ATTACKERS,
    maxStack: number = MAX_STACK_SLOTS,
    options: SimulatorOptions = {},
  ): boolean {
    const sim = new StrategicSimulatorInstance(grid, queue, maxConveyor, maxStack, options);
    return sim.run();
  }

  /** Run simulation and return rich outcome details (win/loss/timeout/stall). */
  static runDetailed(
    grid: CellStack[][],
    queue: AttackerQueueEntry[],
    maxConveyor: number = CONVEYOR_MAX_ATTACKERS,
    maxStack: number = MAX_STACK_SLOTS,
    options: SimulatorOptions = {},
  ): SimulationResult {
    const sim = new StrategicSimulatorInstance(grid, queue, maxConveyor, maxStack, options);
    return sim.runDetailed();
  }

  static runWithTrace(
    grid: CellStack[][],
    queue: AttackerQueueEntry[],
    maxConveyor: number = CONVEYOR_MAX_ATTACKERS,
    maxStack: number = MAX_STACK_SLOTS,
    options: SimulatorOptions = {},
  ): { won: boolean; trace: TraceEntry[] } {
    const trace = createBotTrace();
    const sim = new StrategicSimulatorInstance(grid, queue, maxConveyor, maxStack, options, trace);
    const won = sim.run();
    return { won, trace: trace.entries };
  }

  /**
   * Make a single dispatch decision from mid-game state using the full
   * simulator logic (including rollout search if rolloutDepth > 0).
   *
   * This is the bridge between runtime AutoplaySystem and the simulator —
   * it constructs a simulator instance from live game state, makes ONE
   * dispatch decision, and returns the result.
   */
  static makeDispatchDecision(state: MidGameState): DispatchDecision {
    const sim = StrategicSimulatorInstance.fromMidGameState(state);
    return sim.makeOneDecision();
  }

  /**
   * Run a full simulation from mid-game state with decision recording.
   * Uses high rollout budget for best-quality decisions.
   * Returns both the simulation result and the recorded decision sequence.
   */
  static runAndRecordSolution(state: MidGameState): { result: SimulationResult; solution: DispatchDecision[] } {
    const solveState: MidGameState = {
      ...state,
      rolloutDepth: SOLVE_ROLLOUT_BUDGET,
    };
    const sim = StrategicSimulatorInstance.fromMidGameState(solveState);
    return sim.runWithRecording();
  }
}

class StrategicSimulatorInstance {
  private grid: CellStack[][];
  private rows: number;
  private cols: number;
  private queueLanes: (SimAttacker | null)[][];
  private queueAttackerCount: number;
  private conveyor: SimAttacker[];
  private stack: SimAttacker[];
  private maxConveyor: number;
  private maxStack: number;
  private maxTicks: number;
  private readonly numQueues: number;

  private snakeCells: Set<number>;
  private megaBlocks: Map<number, BotMegaBlock>;
  private megaCells: Set<number>;
  private doorCells: Set<number>;
  private barricadeCells: Set<number>;
  private walls: Map<number, BotWall>;
  private doors: Map<number, BotDoor>;

  private firingLineSequence: FiringLine[];

  private lookaheadDepth: number;
  private subTicks: number;
  private rolloutDepth: number;
  /** How many rollout-evaluated dispatches remain before falling back to heuristic */
  private rolloutBudget: number;

  private trace: BotTrace | undefined;
  private currentTick: number = 0;
  private queueDispatchedThisTick: boolean = false;
  private noProgressLaps: number = 0;

  /** When true, all dispatch decisions are recorded into the decisions array. */
  private recordDecisions: boolean = false;
  /** Recorded dispatch decisions (populated when recordDecisions is true). */
  decisions: DispatchDecision[] = [];

  constructor(
    grid: CellStack[][],
    queue: AttackerQueueEntry[],
    maxConveyor: number,
    maxStack: number,
    options: SimulatorOptions,
    trace?: BotTrace,
  ) {
    this.trace = trace;
    this.rows = grid.length;
    this.cols = this.rows > 0 ? grid[0].length : 0;
    this.grid = grid.map(row => row.map(stack => [...stack]));
    this.numQueues = options.numQueues ?? DEFAULT_NUM_QUEUES;

    // Initialize lane-based queue (same packing as real QueueSystem)
    this.queueLanes = Array.from({ length: this.numQueues }, () => []);
    this.queueAttackerCount = queue.length;
    this.packQueueIntoLanes(queue);

    this.conveyor = [];
    this.stack = [];
    this.maxConveyor = maxConveyor;
    this.maxStack = maxStack;
    this.maxTicks = options.maxTicks ?? 200;
    this.lookaheadDepth = options.lookaheadDepth ?? DEFAULT_LOOKAHEAD_DEPTH;
    // Default 0 means per-line granularity (one sub-tick per firing line),
    // matching runtime behavior where attackers fire continuously each frame.
    this.subTicks = options.subTicks ?? 0;
    this.rolloutDepth = options.rolloutDepth ?? 0;
    this.rolloutBudget = this.rolloutDepth; // Budget = number of rollout dispatches to use

    this.snakeCells = new Set(options.snakeCells ?? []);
    this.doorCells = new Set(options.doorCells ?? []);
    this.barricadeCells = new Set(options.barricadeCells ?? []);

    this.megaBlocks = new Map();
    this.megaCells = new Set();
    if (options.megaBlockDefs) {
      for (let i = 0; i < options.megaBlockDefs.length; i++) {
        const def = options.megaBlockDefs[i];
        this.megaBlocks.set(i, {
          id: i,
          colorIndex: def.colorIndex,
          hp: def.hp,
          row: def.row,
          col: def.col,
          width: def.width,
          height: def.height,
        });
        for (let r = def.row; r < def.row + def.height; r++) {
          for (let c = def.col; c < def.col + def.width; c++) {
            this.megaCells.add(cellKey(r, c));
          }
        }
      }
    }

    // Blockade walls — bot sim must honor these or it massively overestimates
    // reachable cubes from the wall's side. 1-based ids mirror BoardSystem.
    this.walls = new Map();
    if (options.blockadeWallDefs) {
      for (let i = 0; i < options.blockadeWallDefs.length; i++) {
        const def = options.blockadeWallDefs[i];
        const id = i + 1;
        this.walls.set(id, {
          id,
          side: def.side,
          startCell: def.startCell,
          length: def.length,
          colorIndex: def.colorIndex ?? 0,
          hp: def.hp,
        });
      }
    }

    // Locked doors — track per-door HP + trigger color for hit accounting.
    // Door cells themselves stay transparent (doorCells already covers them).
    // 1-based ids mirror BoardSystem.addLockedDoor allocation order.
    this.doors = new Map();
    if (options.lockedDoorDefs) {
      for (let i = 0; i < options.lockedDoorDefs.length; i++) {
        const def = options.lockedDoorDefs[i];
        const id = i + 1;
        this.doors.set(id, {
          id,
          triggerColor: def.colorIndex ?? 0,
          hp: def.hp,
        });
      }
    }

    // Use shared conveyor-path firing sequence from BotStrategy
    this.firingLineSequence = buildFiringSequence(this.rows, this.cols);
  }

  private toSimAttacker(entry: AttackerQueueEntry): SimAttacker {
    const orbs: SimOrb[] = entry.orbs
      ? entry.orbs.map(o => ({ colorIndex: o.colorIndex, ammo: o.ammo }))
      : [{ colorIndex: entry.colorIndex, ammo: entry.ammo }];
    return {
      orbs,
      size: entry.size ?? 1,
      frozen: entry.frozen ?? 0,
    };
  }

  // ── Lane-based Queue ─────────────────────────────────────────────

  private packQueueIntoLanes(queue: AttackerQueueEntry[]): void {
    const laneDepths = Array(this.numQueues).fill(0);

    for (const entry of queue) {
      const att = this.toSimAttacker(entry);
      const size = att.size;

      let bestStart = 0;
      let bestMaxDepth = Infinity;
      for (let start = 0; start <= this.numQueues - size; start++) {
        let maxD = 0;
        for (let i = 0; i < size; i++) {
          maxD = Math.max(maxD, laneDepths[start + i]);
        }
        if (maxD < bestMaxDepth) {
          bestMaxDepth = maxD;
          bestStart = start;
        }
      }

      const lanes = Array.from({ length: size }, (_, i) => bestStart + i);
      const maxDepth = Math.max(...lanes.map(l => laneDepths[l]));
      for (const lane of lanes) {
        while (this.queueLanes[lane].length < maxDepth) {
          this.queueLanes[lane].push(null);
        }
        this.queueLanes[lane].push(att);
        laneDepths[lane] = this.queueLanes[lane].length;
      }
    }

    this.collapseLanes();
  }

  private collapseLanes(): void {
    for (let l = 0; l < this.numQueues; l++) {
      this.queueLanes[l] = this.queueLanes[l].filter(a => a !== null);
    }
  }

  private peekLane(laneIndex: number): SimAttacker | null {
    const lane = this.queueLanes[laneIndex];
    if (lane.length === 0 || lane[0] === null) return null;
    const att = lane[0];
    if (att.frozen > 0) return null;
    if (!this.isReadyToDispatch(att)) return null;
    return att;
  }

  private isReadyToDispatch(att: SimAttacker): boolean {
    for (let l = 0; l < this.numQueues; l++) {
      const lane = this.queueLanes[l];
      if (lane.includes(att) && lane[0] !== att) return false;
    }
    return true;
  }

  private dequeueAttacker(att: SimAttacker): void {
    for (let l = 0; l < this.numQueues; l++) {
      const lane = this.queueLanes[l];
      const idx = lane.indexOf(att);
      if (idx !== -1) {
        lane.splice(idx, 1);
      }
    }
    this.queueAttackerCount--;
  }

  private queueEmpty(): boolean {
    return this.queueAttackerCount <= 0;
  }

  // ── Helpers: convert sim state to BotStrategy types ────────────

  private toBotAttacker(att: SimAttacker): BotAttacker {
    return {
      orbs: att.orbs.map(o => ({ colorIndex: o.colorIndex, ammo: o.ammo })),
      size: att.size,
    };
  }

  private getBoardSnapshot(): BoardSnapshot {
    return {
      grid: this.grid,
      rows: this.rows,
      cols: this.cols,
      snakeCells: this.snakeCells,
      megaBlocks: this.megaBlocks,
      megaCells: this.megaCells,
      doorCells: this.doorCells,
      barricadeCells: this.barricadeCells,
      walls: this.walls,
      doors: this.doors,
    };
  }

  // ── Deep Clone (for rollout search) ──────────────────────────────

  /**
   * Create a deep clone of this simulator instance for rollout evaluation.
   * The clone uses heuristic-only dispatch (rolloutDepth=0) to avoid recursion.
   */
  private cloneInstance(): StrategicSimulatorInstance {
    // We need to deep-clone SimAttackers while preserving multi-orb shared references.
    // Multi-orb attackers appear in multiple lanes pointing to the same SimAttacker object.
    // Use a Map to deduplicate during cloning.
    const attackerCloneMap = new Map<SimAttacker, SimAttacker>();
    const cloneSimAttacker = (att: SimAttacker): SimAttacker => {
      const existing = attackerCloneMap.get(att);
      if (existing) return existing;
      const cloned: SimAttacker = {
        orbs: att.orbs.map(o => ({ colorIndex: o.colorIndex, ammo: o.ammo })),
        size: att.size,
        frozen: att.frozen,
      };
      attackerCloneMap.set(att, cloned);
      return cloned;
    };

    // We can't use the normal constructor (it takes grid + queue), so we'll
    // create a minimal instance and manually copy all fields.
    // Use Object.create to get an instance without calling constructor.
    const clone = Object.create(StrategicSimulatorInstance.prototype) as StrategicSimulatorInstance;

    // Deep clone grid
    clone.grid = this.grid.map(row => row.map(stack => [...stack]));
    clone.rows = this.rows;
    clone.cols = this.cols;

    // Deep clone queue lanes (preserving shared references for multi-orb)
    clone.queueLanes = this.queueLanes.map(lane =>
      lane.map(att => att ? cloneSimAttacker(att) : null)
    );
    clone.queueAttackerCount = this.queueAttackerCount;

    // Deep clone conveyor and stack
    clone.conveyor = this.conveyor.map(att => cloneSimAttacker(att));
    clone.stack = this.stack.map(att => cloneSimAttacker(att));

    // Copy config
    clone.maxConveyor = this.maxConveyor;
    clone.maxStack = this.maxStack;
    clone.maxTicks = this.maxTicks;
    // Use Object.defineProperty to set readonly numQueues
    Object.defineProperty(clone, 'numQueues', { value: this.numQueues, writable: false, enumerable: true, configurable: true });
    clone.lookaheadDepth = this.lookaheadDepth;
    clone.subTicks = this.subTicks;
    // Force rolloutDepth=0 and rolloutBudget=0 in clone to use greedy heuristic (no recursive rollouts)
    clone.rolloutDepth = 0;
    clone.rolloutBudget = 0;

    // Share immutable references
    clone.snakeCells = this.snakeCells;
    clone.doorCells = this.doorCells;
    clone.barricadeCells = this.barricadeCells;
    clone.firingLineSequence = this.firingLineSequence;

    // Deep clone mega blocks
    clone.megaBlocks = new Map();
    clone.megaCells = new Set(this.megaCells);
    for (const [id, mb] of this.megaBlocks) {
      clone.megaBlocks.set(id, { ...mb });
    }

    // Deep clone walls — per-clone HP state so rollouts don't corrupt parent
    clone.walls = new Map();
    for (const [id, w] of this.walls) {
      clone.walls.set(id, { ...w });
    }

    // Deep clone doors — per-clone HP state
    clone.doors = new Map();
    for (const [id, d] of this.doors) {
      clone.doors.set(id, { ...d });
    }

    // No trace in clones, no decision recording
    clone.trace = undefined;
    clone.currentTick = 0;
    clone.queueDispatchedThisTick = false;
    clone.noProgressLaps = 0;
    clone.recordDecisions = false;
    clone.decisions = [];

    return clone;
  }

  // ── Mid-Game State Construction ────────────────────────────────────

  /**
   * Create a StrategicSimulatorInstance from mid-game state (for runtime autoplay).
   * Similar to cloneInstance() but initializes from external state rather than
   * copying an existing instance.
   */
  static fromMidGameState(state: MidGameState): StrategicSimulatorInstance {
    const inst = Object.create(StrategicSimulatorInstance.prototype) as StrategicSimulatorInstance;

    const rows = state.grid.length;
    const cols = rows > 0 ? state.grid[0].length : 0;
    inst.rows = rows;
    inst.cols = cols;
    inst.grid = state.grid.map(row => row.map(stack => [...stack]));

    Object.defineProperty(inst, 'numQueues', {
      value: state.numQueues, writable: false, enumerable: true, configurable: true,
    });

    // Copy queue lanes (deep copy attackers, preserve shared refs for multi-orb)
    const attackerMap = new Map<SimAttackerData, SimAttacker>();
    const toSim = (sad: SimAttackerData): SimAttacker => {
      const existing = attackerMap.get(sad);
      if (existing) return existing;
      const sim: SimAttacker = {
        orbs: sad.orbs.map(o => ({ colorIndex: o.colorIndex, ammo: o.ammo })),
        size: sad.size,
        frozen: sad.frozen,
      };
      attackerMap.set(sad, sim);
      return sim;
    };

    inst.queueLanes = state.queueLanes.map(lane =>
      lane.map(att => att ? toSim(att) : null)
    );

    // Count unique queue attackers (multi-orb attackers appear in multiple lanes)
    const uniqueQueue = new Set<SimAttacker>();
    for (const lane of inst.queueLanes) {
      for (const att of lane) {
        if (att) uniqueQueue.add(att);
      }
    }
    inst.queueAttackerCount = uniqueQueue.size;

    // Copy conveyor and stack
    inst.conveyor = state.conveyor.map(a => ({
      orbs: a.orbs.map(o => ({ colorIndex: o.colorIndex, ammo: o.ammo })),
      size: a.size,
      frozen: a.frozen,
    }));
    inst.stack = state.stack.map(a => ({
      orbs: a.orbs.map(o => ({ colorIndex: o.colorIndex, ammo: o.ammo })),
      size: a.size,
      frozen: a.frozen,
    }));

    // Config
    inst.maxConveyor = state.maxConveyor;
    inst.maxStack = state.maxStack;
    inst.maxTicks = 200;
    inst.lookaheadDepth = state.lookaheadDepth ?? DEFAULT_LOOKAHEAD_DEPTH;
    inst.subTicks = 0;
    inst.rolloutDepth = state.rolloutDepth ?? 0;
    inst.rolloutBudget = inst.rolloutDepth;

    // Special cells
    inst.snakeCells = state.snakeCells ? new Set(state.snakeCells) : new Set();
    inst.doorCells = state.doorCells ? new Set(state.doorCells) : new Set();
    inst.barricadeCells = state.barricadeCells ? new Set(state.barricadeCells) : new Set();

    // Mega blocks
    inst.megaBlocks = new Map();
    inst.megaCells = new Set();
    if (state.megaBlockDefs) {
      for (let i = 0; i < state.megaBlockDefs.length; i++) {
        const def = state.megaBlockDefs[i];
        inst.megaBlocks.set(i, {
          id: i,
          colorIndex: def.colorIndex,
          hp: def.hp,
          row: def.row,
          col: def.col,
          width: def.width,
          height: def.height,
        });
        for (let r = def.row; r < def.row + def.height; r++) {
          for (let c = def.col; c < def.col + def.width; c++) {
            inst.megaCells.add(cellKey(r, c));
          }
        }
      }
    }

    // Blockade walls (mid-game state provides current HP per wall)
    inst.walls = new Map();
    if (state.blockadeWalls) {
      for (const w of state.blockadeWalls) {
        inst.walls.set(w.id, { ...w });
      }
    }

    // Locked doors (mid-game state provides current HP per door)
    inst.doors = new Map();
    if (state.lockedDoors) {
      for (const d of state.lockedDoors) {
        inst.doors.set(d.id, { ...d });
      }
    }

    // Firing sequence
    inst.firingLineSequence = buildFiringSequence(rows, cols);

    // No trace for runtime decisions, no decision recording by default
    inst.trace = undefined;
    inst.currentTick = 0;
    inst.queueDispatchedThisTick = false;
    inst.noProgressLaps = 0;
    inst.recordDecisions = false;
    inst.decisions = [];

    return inst;
  }

  /**
   * Make a single dispatch decision and return what action was taken.
   * Snapshots state before tryDispatch(), then diffs to determine the action.
   */
  makeOneDecision(): DispatchDecision {
    const stackBefore = [...this.stack];
    const queueCountBefore = this.queueAttackerCount;
    const queueLaneLengths = this.queueLanes.map(l => l.length);

    const dispatched = this.tryDispatch();
    if (!dispatched) return { action: 'wait' };

    // Stack dispatch: stack shrank
    if (this.stack.length < stackBefore.length) {
      for (let i = 0; i < stackBefore.length; i++) {
        if (i >= this.stack.length || this.stack[i] !== stackBefore[i]) {
          return { action: 'dispatch_stack', index: i };
        }
      }
    }

    // Queue dispatch: queue count decreased
    if (this.queueAttackerCount < queueCountBefore) {
      for (let lane = 0; lane < this.numQueues; lane++) {
        if (this.queueLanes[lane].length < queueLaneLengths[lane]) {
          return { action: 'dispatch_queue', index: lane };
        }
      }
      // Multi-orb fallback: attacker removed from multiple lanes simultaneously
      return { action: 'dispatch_queue', index: 0 };
    }

    return { action: 'wait' };
  }

  /**
   * Run the full simulation with decision recording enabled.
   * Returns both the simulation result and the recorded decisions.
   */
  runWithRecording(): { result: SimulationResult; solution: DispatchDecision[] } {
    this.recordDecisions = true;
    this.decisions = [];
    const result = this.runDetailed();
    return { result, solution: this.decisions };
  }

  /**
   * Return a BoardStateSnapshot from the current simulator state.
   * Used by rollout scoring to feed the BoardStateEvaluator.
   */
  private getStateSnapshot(totalCellsAtStart: number): BoardStateSnapshot {
    return {
      board: this.getBoardSnapshot(),
      stack: this.stack.map(a => this.toBotAttacker(a)),
      queue: this.queueLanes.map(lane =>
        lane.filter((a): a is SimAttacker => a !== null).map(a => this.toBotAttacker(a))
      ),
      conveyor: this.conveyor.map(a => this.toBotAttacker(a)),
      maxStackSlots: this.maxStack,
      totalCellsAtStart,
    };
  }

  // ── Rollout-Based Dispatch ──────────────────────────────────────────

  /**
   * Rollout-based dispatch: for each candidate, clone the sim, force-dispatch,
   * run to completion with greedy heuristic, pick the candidate that leads to best outcome.
   * Scoring uses BoardStateEvaluator for richer positional assessment.
   */
  private tryDispatchWithRollout(): boolean {
    const conveyorUsed = this.conveyorSlotsUsed();

    // Build candidates from stack and queue (same logic as tryDispatch)
    const candidates: { simAtt: SimAttacker; source: 'stack' | 'queue'; index: number }[] = [];
    const seen = new Set<SimAttacker>();

    for (let i = 0; i < this.stack.length; i++) {
      const att = this.stack[i];
      if (seen.has(att)) continue;
      seen.add(att);
      if (att.size + conveyorUsed > this.maxConveyor) continue;
      candidates.push({ simAtt: att, source: 'stack', index: i });
    }

    const seenQueue = new Set<SimAttacker>();
    for (let lane = 0; lane < this.numQueues; lane++) {
      const att = this.peekLane(lane);
      if (att && !seenQueue.has(att)) {
        seenQueue.add(att);
        if (att.size + conveyorUsed <= this.maxConveyor) {
          candidates.push({ simAtt: att, source: 'queue', index: lane });
        }
      }
    }

    if (candidates.length === 0) {
      // Fallback: same as tryDispatch — force dispatch if everything empty
      if (conveyorUsed === 0 && this.stack.length === 0 && !this.queueEmpty()) {
        const reachMap = buildReachMap(this.getBoardSnapshot());
        for (let lane = 0; lane < this.numQueues; lane++) {
          const rawLane = this.queueLanes[lane];
          if (rawLane.length === 0 || rawLane[0] === null) continue;
          const att = rawLane[0];
          if (conveyorUsed + att.size > this.maxConveyor) continue;
          const hasReachable = att.orbs.some(
            o => o.ammo > 0 && (reachMap.get(o.colorIndex) ?? 0) > 0
          );
          if (!hasReachable) continue;
          this.dequeueAttacker(att);
          this.conveyor.push(att);
          this.recordDecision({ action: 'dispatch_queue', index: lane });
          return true;
        }
      }
      return false;
    }

    // If only one candidate, skip rollout and just dispatch it
    if (candidates.length === 1) {
      const cand = candidates[0];
      if (cand.source === 'stack') {
        this.stack.splice(cand.index, 1);
        this.conveyor.push(cand.simAtt);
        this.recordDecision({ action: 'dispatch_stack', index: cand.index });
      } else {
        this.dequeueAttacker(cand.simAtt);
        this.conveyor.push(cand.simAtt);
        this.queueDispatchedThisTick = true;
        this.recordDecision({ action: 'dispatch_queue', index: cand.index });
      }
      return true;
    }

    // Evaluate each candidate via full-game rollout
    let bestCand: typeof candidates[0] | null = null;
    let bestScore = -Infinity;

    // Count total cells at start for evaluator (approximate from current state)
    const totalCellsForEval = this.countCells();

    for (const cand of candidates) {
      const clone = this.cloneInstance();

      // Force-dispatch the candidate in the clone
      if (cand.source === 'stack') {
        clone.stack.splice(cand.index, 1);
        const clonedAtt = this.cloneSimAttackerForRollout(cand.simAtt);
        clone.conveyor.push(clonedAtt);
      } else {
        const clonedLane = clone.queueLanes[cand.index];
        if (clonedLane.length > 0 && clonedLane[0]) {
          const clonedAtt = clonedLane[0];
          for (let l = 0; l < clone.queueLanes.length; l++) {
            const lane = clone.queueLanes[l];
            const idx = lane.indexOf(clonedAtt);
            if (idx !== -1) lane.splice(idx, 1);
          }
          clone.queueAttackerCount--;
          clone.conveyor.push(clonedAtt);
        }
      }

      // Run the clone to completion using greedy heuristic
      const result = clone.runDetailed();

      // Score using BoardStateEvaluator for richer positional assessment
      const snapshot = clone.getStateSnapshot(totalCellsForEval);
      const boardStateScore = evaluateBoardState(snapshot);

      const score = (result.outcome === 'win' ? 1_000_000 : 0) + boardStateScore;

      if (score > bestScore) {
        bestScore = score;
        bestCand = cand;
      }
    }

    if (!bestCand) return false;

    // Apply the winning dispatch to the real state
    if (bestCand.source === 'stack') {
      const att = this.stack[bestCand.index];
      this.stack.splice(bestCand.index, 1);
      this.conveyor.push(att);
      this.recordDecision({ action: 'dispatch_stack', index: bestCand.index });
      return true;
    } else {
      const att = this.peekLane(bestCand.index);
      if (att) {
        this.dequeueAttacker(att);
        this.conveyor.push(att);
        this.queueDispatchedThisTick = true;
        this.recordDecision({ action: 'dispatch_queue', index: bestCand.index });
        return true;
      }
    }

    return false;
  }

  /**
   * Clone a single SimAttacker (used by rollout to place candidate in clone's conveyor).
   */
  private cloneSimAttackerForRollout(att: SimAttacker): SimAttacker {
    return {
      orbs: att.orbs.map(o => ({ colorIndex: o.colorIndex, ammo: o.ammo })),
      size: att.size,
      frozen: att.frozen,
    };
  }

  // ── Main Loop ──────────────────────────────────────────────────────

  /** Stall threshold: consecutive zero-progress laps before declaring stall. */
  private static readonly STALL_THRESHOLD = 5;

  run(): boolean {
    return this.runDetailed().outcome === 'win';
  }

  /** Run simulation with rich outcome tracking. */
  runDetailed(): SimulationResult {
    const totalCellsAtStart = this.countCells();

    if (this.isBoardEmpty()) {
      return {
        outcome: 'win', lapsExecuted: 0, totalCells: totalCellsAtStart,
        cellsRemaining: 0, percentCleared: 100,
      };
    }

    let noProgressLaps = 0;

    // Per-line sub-tick model matching runtime conveyor traversal.
    const totalLines = this.firingLineSequence.length;

    for (let tick = 0; tick < this.maxTicks; tick++) {
      this.currentTick = tick;
      this.queueDispatchedThisTick = false;
      const cellsBefore = this.countCells();

      // Early loss detection: if no available attacker color is reachable, declare loss
      if (!this.isBoardEmpty() && !this.hasAnyUsefulAttacker()) {
        const rem = this.countCells();
        return {
          outcome: 'loss', lapsExecuted: tick, totalCells: totalCellsAtStart,
          cellsRemaining: rem, percentCleared: totalCellsAtStart > 0 ? Math.round((1 - rem / totalCellsAtStart) * 100) : 100,
          stackSize: this.stack.length, queueRemaining: this.queueAttackerCount,
        };
      }

      // Fill conveyor to capacity before starting the lap
      const decisionsBeforeFill = this.decisions.length;
      while (this.conveyorSlotsUsed() < this.maxConveyor) {
        if (!this.tryDispatch()) break;
      }
      // Record a single wait if no dispatch happened this tick
      if (this.recordDecisions && this.decisions.length === decisionsBeforeFill) {
        this.decisions.push({ action: 'wait' });
      }

      // Per-line firing loop
      for (let lineIdx = 0; lineIdx < totalLines; lineIdx++) {
        this.fireSegment(lineIdx, lineIdx + 1);

        const slotsBeforeExhaust = this.conveyor.length;
        this.exhaustAttackers();
        const slotFreed = this.conveyor.length < slotsBeforeExhaust;

        if (this.isBoardEmpty()) {
          return {
            outcome: 'win', lapsExecuted: tick + 1, totalCells: totalCellsAtStart,
            cellsRemaining: 0, percentCleared: 100,
          };
        }

        if (slotFreed) {
          while (this.conveyorSlotsUsed() < this.maxConveyor) {
            if (!this.tryDispatch()) break;
          }
        }
      }

      if (this.isBoardEmpty()) {
        return {
          outcome: 'win', lapsExecuted: tick + 1, totalCells: totalCellsAtStart,
          cellsRemaining: 0, percentCleared: 100,
        };
      }

      // Lap complete — move remaining conveyor attackers to stack
      const overflow = this.lapComplete();
      if (overflow) {
        const rem = this.countCells();
        return {
          outcome: 'loss', lapsExecuted: tick + 1, totalCells: totalCellsAtStart,
          cellsRemaining: rem, percentCleared: totalCellsAtStart > 0 ? Math.round((1 - rem / totalCellsAtStart) * 100) : 100,
          stackSize: this.stack.length, queueRemaining: this.queueAttackerCount,
        };
      }

      // Stuck check
      if (this.conveyor.length === 0 && this.queueEmpty()) {
        if (!this.hasUsefulStackAttacker()) {
          const rem = this.countCells();
          return {
            outcome: 'loss', lapsExecuted: tick + 1, totalCells: totalCellsAtStart,
            cellsRemaining: rem, percentCleared: totalCellsAtStart > 0 ? Math.round((1 - rem / totalCellsAtStart) * 100) : 100,
            stackSize: this.stack.length, queueRemaining: this.queueAttackerCount,
          };
        }
      }

      // Progress detection — stall vs normal loss
      const cellsAfter = this.countCells();
      if (cellsAfter >= cellsBefore) {
        noProgressLaps++;
        this.noProgressLaps = noProgressLaps;
        if (noProgressLaps >= StrategicSimulatorInstance.STALL_THRESHOLD) {
          const rem = this.countCells();
          return {
            outcome: 'stall', lapsExecuted: tick + 1, totalCells: totalCellsAtStart,
            cellsRemaining: rem, percentCleared: totalCellsAtStart > 0 ? Math.round((1 - rem / totalCellsAtStart) * 100) : 100,
            stallLaps: noProgressLaps,
            stackSize: this.stack.length, queueRemaining: this.queueAttackerCount,
          };
        }
      } else {
        noProgressLaps = 0;
        this.noProgressLaps = 0;
      }
    }

    // Hit maxTicks — timeout
    const rem = this.countCells();
    return {
      outcome: 'timeout', lapsExecuted: this.maxTicks, totalCells: totalCellsAtStart,
      cellsRemaining: rem, percentCleared: totalCellsAtStart > 0 ? Math.round((1 - rem / totalCellsAtStart) * 100) : 100,
      stackSize: this.stack.length, queueRemaining: this.queueAttackerCount,
    };
  }

  // ── Decision Recording ──────────────────────────────────────────

  private recordDecision(decision: DispatchDecision): void {
    if (this.recordDecisions) {
      this.decisions.push(decision);
    }
  }

  // ── Dispatch (using BotStrategy depth-2 lookahead) ─────────────

  private tryDispatch(): boolean {
    // Use rollout-based dispatch when budget remains
    if (this.rolloutBudget > 0) {
      const result = this.tryDispatchWithRollout();
      if (result) this.rolloutBudget--;
      return result;
    }

    const conveyorUsed = this.conveyorSlotsUsed();
    const stackUsed = this.stackSlotsUsed();

    // Build candidates from stack and queue
    const candidates: DispatchCandidate[] = [];
    const seen = new Set<SimAttacker>();

    for (let i = 0; i < this.stack.length; i++) {
      const att = this.stack[i];
      if (seen.has(att)) continue;
      seen.add(att);
      candidates.push({
        source: 'stack',
        index: i,
        attacker: this.toBotAttacker(att),
      });
    }

    // Cap queue dispatch rate: if we already dispatched from queue this tick
    // and stack has any occupants, don't add queue candidates. This forces the
    // bot to fill conveyor slots from stack (recycling) rather than pulling
    // more new attackers that will eventually need stack slots.
    // Stall override: when noProgressLaps >= 3, force queue candidates into
    // the pool even when suppressQueue would block them — gives the bot a
    // chance to break deadlocks by pulling fresh colors from the queue.
    const stallOverride = this.noProgressLaps >= 3;
    const suppressQueue = !stallOverride && this.queueDispatchedThisTick && stackUsed >= 1;

    const queueFronts: (BotAttacker | null)[] = [];
    const seenQueue = new Set<SimAttacker>();
    for (let lane = 0; lane < this.numQueues; lane++) {
      const att = this.peekLane(lane);
      if (att && !seenQueue.has(att) && !suppressQueue) {
        seenQueue.add(att);
        const botAtt = this.toBotAttacker(att);
        candidates.push({
          source: 'queue',
          index: lane,
          attacker: botAtt,
        });
        queueFronts.push(botAtt);
      } else {
        queueFronts.push(null);
      }
    }

    if (candidates.length === 0) {
      // Fallback: if conveyor and stack are both empty and queue has items,
      // dispatch even if frozen (to avoid deadlock).
      // BUT only dispatch queue candidates whose color is currently reachable —
      // dispatching unreachable-color attackers just wastes stack slots.
      if (conveyorUsed === 0 && this.stack.length === 0 && !this.queueEmpty()) {
        const reachMap = buildReachMap(this.getBoardSnapshot());
        for (let lane = 0; lane < this.numQueues; lane++) {
          // peekLane skips frozen, so check raw front
          const rawLane = this.queueLanes[lane];
          if (rawLane.length === 0 || rawLane[0] === null) continue;
          const att = rawLane[0];
          if (conveyorUsed + att.size > this.maxConveyor) continue;
          // Skip if none of this attacker's orbs target reachable cells
          const hasReachable = att.orbs.some(
            o => o.ammo > 0 && (reachMap.get(o.colorIndex) ?? 0) > 0
          );
          if (!hasReachable) continue;
          this.dequeueAttacker(att);
          this.conveyor.push(att);
          this.recordDecision({ action: 'dispatch_queue', index: lane });
          return true;
        }
      }
      return false;
    }

    const stackBotAttackers = this.stack.map(a => this.toBotAttacker(a));
    const conveyorBotAttackers = this.conveyor.map(a => this.toBotAttacker(a));

    // Build deep queue lanes for lookahead (up to lookaheadDepth deep)
    const botQueueLanes: (BotAttacker | null)[][] = [];
    for (let lane = 0; lane < this.numQueues; lane++) {
      const simLane = this.queueLanes[lane];
      const botLane: (BotAttacker | null)[] = [];
      for (let d = 0; d < Math.min(simLane.length, this.lookaheadDepth); d++) {
        const att = simLane[d];
        if (att && att.frozen <= 0) {
          botLane.push(this.toBotAttacker(att));
        } else {
          botLane.push(null);
        }
      }
      botQueueLanes.push(botLane);
    }

    const traceEntriesBefore = this.trace ? this.trace.entries.length : 0;

    const decision = evaluateDispatch(
      this.getBoardSnapshot(),
      candidates,
      this.firingLineSequence,
      stackBotAttackers,
      queueFronts,
      this.maxConveyor,
      conveyorUsed,
      this.maxStack,
      stackUsed,
      conveyorBotAttackers,
      this.trace,
      this.lookaheadDepth,
      botQueueLanes,
      undefined,
    );

    // Set tick on any newly added trace entry
    if (this.trace && this.trace.entries.length > traceEntriesBefore) {
      this.trace.entries[this.trace.entries.length - 1].tick = this.currentTick;
    }

    if (!decision) {
      // Fallback: if conveyor and stack are both empty and queue has items.
      // Only dispatch queue candidates with reachable colors.
      if (conveyorUsed === 0 && this.stack.length === 0 && !this.queueEmpty()) {
        const reachMap = buildReachMap(this.getBoardSnapshot());
        for (let lane = 0; lane < this.numQueues; lane++) {
          const att = this.peekLane(lane);
          if (!att) continue;
          if (conveyorUsed + att.size > this.maxConveyor) continue;
          const hasReachable = att.orbs.some(
            o => o.ammo > 0 && (reachMap.get(o.colorIndex) ?? 0) > 0
          );
          if (!hasReachable) continue;
          this.dequeueAttacker(att);
          this.conveyor.push(att);
          this.recordDecision({ action: 'dispatch_queue', index: lane });
          return true;
        }
      }
      return false;
    }

    // Bot chose to wait — skip dispatch, let conveyor lap proceed
    if (decision.action === 'wait') {
      return false;
    }

    const cand = decision.candidate;
    if (cand.source === 'stack') {
      const att = this.stack[cand.index];
      this.stack.splice(cand.index, 1);
      this.conveyor.push(att);
      this.recordDecision({ action: 'dispatch_stack', index: cand.index });
      return true;
    } else {
      // Queue dispatch: find the actual SimAttacker at that lane
      const att = this.peekLane(cand.index);
      if (att) {
        this.dequeueAttacker(att);
        this.conveyor.push(att);
        this.queueDispatchedThisTick = true;
        this.recordDecision({ action: 'dispatch_queue', index: cand.index });
        return true;
      }
    }

    return false;
  }

  // ── Slot counting ───────────────────────────────────────────────

  private conveyorSlotsUsed(): number {
    return this.conveyor.reduce((sum, a) => sum + a.size, 0);
  }

  private stackSlotsUsed(): number {
    return this.stack.reduce((sum, a) => sum + a.size, 0);
  }

  // ── Firing (sub-tick segment model) ─────────────────────────────

  /**
   * Fire a segment of the firing sequence (from startIdx to endIdx) for all
   * conveyor attackers. Used by the sub-tick loop to model mid-lap firing.
   */
  private fireSegment(startIdx: number, endIdx: number): void {
    const board = this.getBoardSnapshot();
    const segment = this.firingLineSequence.slice(startIdx, endIdx);
    for (const att of this.conveyor) {
      simulateLap(board, att, segment);
    }
  }

  // ── Exhaustion & Lap ────────────────────────────────────────────

  private exhaustAttackers(): void {
    const remaining: SimAttacker[] = [];
    for (const att of this.conveyor) {
      if (this.isExhausted(att)) {
        this.thawFrozenQueue();
      } else {
        remaining.push(att);
      }
    }
    this.conveyor = remaining;
  }

  private isExhausted(att: SimAttacker): boolean {
    return att.orbs.every(o => o.ammo <= 0);
  }

  private thawFrozenQueue(): void {
    const seen = new Set<SimAttacker>();
    for (const lane of this.queueLanes) {
      for (const att of lane) {
        if (!att || seen.has(att)) continue;
        seen.add(att);
        if (att.frozen > 0) {
          att.frozen--;
        }
      }
    }
  }

  private lapComplete(): boolean {
    for (const att of this.conveyor) {
      if (this.stackSlotsUsed() + att.size > this.maxStack) {
        return true;
      }
      this.stack.push(att);
    }
    this.conveyor = [];
    return false;
  }

  // ── Board state ─────────────────────────────────────────────────

  private countCells(): number {
    let count = 0;
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        count += this.grid[r][c].length;
      }
    }
    for (const mb of this.megaBlocks.values()) {
      count += mb.hp;
    }
    return count;
  }

  private isBoardEmpty(): boolean {
    if (this.megaBlocks.size > 0) return false;
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        if (this.grid[r][c].length > 0) return false;
      }
    }
    return true;
  }

  private hasUsefulStackAttacker(): boolean {
    const reachMap = buildReachMap(this.getBoardSnapshot());
    for (const att of this.stack) {
      for (const orb of att.orbs) {
        if (orb.ammo <= 0) continue;
        if ((reachMap.get(orb.colorIndex) ?? 0) > 0) return true;
      }
    }
    return false;
  }

  /**
   * Returns false if no available attacker (stack, queue fronts, conveyor)
   * has a color that can reach any cube on the board. When this is true
   * the game is unwinnable — declare loss immediately.
   */
  private hasAnyUsefulAttacker(): boolean {
    // Build a set of ALL colors that exist on the board (reachable or not).
    // Interior colors in large contiguous regions are currently unreachable but
    // WILL become reachable as outer layers clear. Only declare loss if no
    // attacker's color exists on the board at all.
    const boardColors = new Set<number>();
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        for (const ci of this.grid[r][c]) {
          boardColors.add(ci);
        }
      }
    }
    for (const mb of this.megaBlocks.values()) {
      boardColors.add(mb.colorIndex);
    }

    const isUseful = (colorIndex: number, ammo: number) =>
      ammo > 0 && boardColors.has(colorIndex);

    // Check conveyor
    for (const att of this.conveyor) {
      for (const orb of att.orbs) {
        if (isUseful(orb.colorIndex, orb.ammo)) return true;
      }
    }

    // Check stack
    for (const att of this.stack) {
      for (const orb of att.orbs) {
        if (isUseful(orb.colorIndex, orb.ammo)) return true;
      }
    }

    // Check queue fronts (one per lane)
    const seen = new Set<SimAttacker>();
    for (let lane = 0; lane < this.numQueues; lane++) {
      const laneArr = this.queueLanes[lane];
      for (const att of laneArr) {
        if (!att || seen.has(att)) continue;
        seen.add(att);
        for (const orb of att.orbs) {
          if (isUseful(orb.colorIndex, orb.ammo)) return true;
        }
        break; // only check front of each lane
      }
    }

    return false;
  }
}
